import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PipHost from "./PipHost";
import CastDockCard from "./CastDockCard";
import OwnCastControls from "./OwnCastControls";
import WatchedCastControls from "./WatchedCastControls";
import WallDockCard from "./WallDockCard";
import ChatLinkDockCard, { chatMessagePath } from "./ChatLinkDockCard";
import MiniPlayerInfo from "./MiniPlayerInfo";
import { useGoToWallPost } from "../wall/wallJump";
import { AVOID_CORNER, CARD_WIDTH, MINI_SWIPE_OPEN, MOBILE_DOCK_BOTTOM, MOBILE_DOCK_GAP, MOBILE_DOCK_TOP, PIP_WINDOW_SIZE, cornerStyle } from "./dockLayout";
import { useDockAvoidStore } from "../../store/dockAvoidStore";
import type { PipCorner } from "../../store/streamPipStore";
import { useStreamPipStore } from "../../store/streamPipStore";
import { useWallPlaybackStore } from "../../store/wallPlaybackStore";
import { useChatLinkPlaybackStore, type ChatLinkNowPlaying } from "../../store/chatLinkPlaybackStore";
import { useDockMiniStore } from "../../store/dockMiniStore";
import { CHAT_LINK_KEY, LIVE_KEY, WALL_KEY, miniPlayerKey, type DockPlayerRef } from "../../lib/dockMini";
import { slotEntries, slotLabel } from "../../lib/streamSlot";
import { findSlot, useStreamStatusStore } from "../../store/streamStatusStore";
// Importing the store module also registers its status-store subscription
// (first-seen times, auto-subscribe), so it is live whenever the dock is.
import "../../store/castDockStore";
import { useOwnCastLifecycle } from "../../hooks/useOwnCastLifecycle";
import { useDockCasts } from "../../hooks/useDockCasts";
import { useCastRecents } from "../../hooks/useCastRecents";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePipDrag } from "../../hooks/usePipDrag";
import { castKeyOf, pipTargetOf, type DockCast } from "../../lib/castDock";
import { acceptsTimeline, timelineOf, type CastTimeline } from "../../lib/castSync/timeline";
import { useOwnCastStore } from "../../store/ownCastStore";
import { useCastQueueStore } from "../../store/castQueueStore";
import { linkItemOf, withQueueItem } from "../../lib/castQueue";
import { kindOf } from "../../player";
import { isDocumentPipSupported, openPipWindow } from "../../lib/documentPip";
import type { StreamSlot } from "../../types/stream";
import logger from "../../lib/logger";
import { Z_DOCK } from "../../lib/zLayers";

/**
 * The one floating surface: the live mini card (PipHost, docked) on top, then
 * one card per cast in dock order, then the wall playback card (#32). The frame owns position, corner, drag and
 * size; each child owns its own player.
 *
 * Minimised (owner 2026-10-21), the frame is the bottom mini player: one player shows (lib/dockMini priority) with
 * "+N", the others stay mounted but hidden. Figma: Web / Dock / Mini player 4308:11316, 4308:11397, 4308:11560,
 * 4308:11595 (component 4304:10630).
 */
export default function StreamDock() {
  const navigate = useNavigate();
  // #3: the wall card's Go to post; also opens the post dialog when its wall cannot show it.
  const goToWallPost = useGoToWallPost();
  const isMobile = useIsMobile();
  const casts = useDockCasts();
  const infoByGc = useStreamStatusStore((s) => s.infoByGc);
  const ownCasts = useOwnCastStore((s) => s.casts);
  const queues = useCastQueueStore((s) => s.bySlot);
  // #32: the wall card (one at a time) stacks under the casts and never pops out (pipTargetOf ignores it).
  const wall = useWallPlaybackStore((s) => s.current);
  // Link kinds step 2: a chat message's link (never together with the wall card).
  const chatLink = useChatLinkPlaybackStore((s) => s.current);
  // Same condition PipHost renders on: not on the panel and a session exists.
  const liveShowing = useStreamPipStore(
    (s) => s.mode !== "panel" && s.focusedSlotId != null && s.sessions[s.focusedSlotId] != null,
  );
  const livePopped = useStreamPipStore((s) => s.mode === "popped");
  const liveSlotId = useStreamPipStore((s) => s.focusedSlotId);
  const minimised = useDockMiniStore((s) => s.minimised);
  const preferred = useDockMiniStore((s) => s.preferred);
  const corner = useStreamPipStore((s) => s.corner);
  const size = useStreamPipStore((s) => s.size);
  const setCorner = useStreamPipStore((s) => s.setCorner);
  const [popped, setPopped] = useState<{ key: string; win: Window } | null>(null);
  const [liveSlotEl, setLiveSlotEl] = useState<HTMLDivElement | null>(null);
  // A post dialog is open under the dock: move out of the way of its controls (bottom on a phone, top-left on
  // desktop). A drag meanwhile wins until the dialog closes; then the saved corner is back, untouched.
  const avoiding = useDockAvoidStore((s) => s.count > 0);
  const [avoidCorner, setAvoidCorner] = useState<PipCorner | null>(null);
  useEffect(() => {
    if (!avoiding) setAvoidCorner(null);
  }, [avoiding]);
  const shownCorner: PipCorner = avoiding ? (avoidCorner ?? AVOID_CORNER) : corner;
  const drag = usePipDrag({ enabled: !isMobile, onSnap: avoiding ? setAvoidCorner : setCorner });
  const target = pipTargetOf(liveShowing, casts);

  // Own casts: tab-close and logout stops (logout also resets the dock store), the slot list's
  // stops and timelines, orphan reconcile after each slot load, refresh after an own stop.
  useOwnCastLifecycle();
  // #32: a cast of a post counts once per cast in Recent plays.
  useCastRecents();

  // Closing the PiP window returns the card to the dock. Leaving this popped
  // state any other way (unmount included) closes the window, so no blank
  // always-on-top OS window is left behind.
  useEffect(() => {
    if (!popped) return;
    const onClosed = () => setPopped(null);
    popped.win.addEventListener("pagehide", onClosed);
    return () => {
      popped.win.removeEventListener("pagehide", onClosed);
      popped.win.close();
    };
  }, [popped]);

  // The popped cast left the dock (slot freed, hidden, logout): drop it, which closes its window.
  const poppedKey = popped?.key;
  const poppedStillDocked = poppedKey != null && casts.some((c) => c.key === poppedKey);
  useEffect(() => {
    if (popped && !poppedStillDocked) setPopped(null);
  }, [popped, poppedStillDocked]);

  // PipHost stays mounted at one place whatever the frame shows: its effects
  // (logout reset, closing a stale Document PiP window) must run while the
  // live card is on the panel or gone. It portals into the frame's live slot.
  const liveInline = liveShowing && !livePopped;
  const inlineCasts = casts.filter((c) => c.key !== popped?.key).length;
  const frameVisible = liveInline || inlineCasts > 0 || wall != null || chatLink != null;

  const slotOf = (cast: DockCast): StreamSlot | undefined => {
    const status = findSlot(infoByGc, cast.slotId)?.slot;
    if (!cast.own) return status;
    const mine = ownCasts[cast.localId];
    if (!mine) return status;
    // The slot list's copy while it still shows this very cast; else the own-cast record.
    const base = status && castKeyOf(status) === castKeyOf(mine.slot) ? status : mine.slot;
    // The queue item (the answer's) first: it is compared against the list's rev, then the timeline.
    return withNewerTimeline(withQueueItem(base, queues[cast.slotId]), mine.timeline);
  };

  // The players the app window shows, for the mini player's pick (a popped-out cast lives in its own window).
  const players: DockPlayerRef[] = [];
  if (liveInline) players.push({ key: LIVE_KEY, kind: "live" });
  for (const cast of casts) {
    const slot = cast.key === popped?.key ? undefined : slotOf(cast);
    if (!slot) continue;
    // Your own link cast on an item that must not play unseen (YouTube / SoundCloud) ends items for everyone.
    const held = cast.localId === "VMPL_LINK_1" && kindOf(linkItemOf(slot, queues[cast.slotId]?.queue ?? null)).caps.heldWhenHidden;
    players.push({ key: cast.key, kind: cast.own ? "own" : "watched", drivesItems: cast.own && held });
  }
  if (wall) players.push({ key: WALL_KEY, kind: "wall" });
  if (chatLink) players.push({ key: CHAT_LINK_KEY, kind: "chatLink" });
  const topKey = minimised ? miniPlayerKey(players, preferred) : null;
  const mini = topKey != null;
  const openDock = () => useDockMiniStore.getState().open();
  const miniFor = (key: string) => (key === topKey ? { others: players.length - 1, onOpen: openDock } : null);
  const liveTop = topKey === LIVE_KEY;

  const pipHost = <PipHost docked container={liveSlotEl} onDragHandlePointerDown={mini ? undefined : drag.onPointerDown} />;

  // Over the screen's foot — phone, avoiding (moved to the bottom, over the panel's foot), or the mini player (phone or
  // desktop) — publish where it is: its rect, so chat inputs pad clear only where they overlap it (useDockClearance);
  // and on a phone how far up it reaches, so the in-panel composer and the page content pad their end. bottomInset
  // stays 0 on desktop; both reset when the frame leaves the foot.
  const [frameEl, setFrameEl] = useState<HTMLDivElement | null>(null);
  const covering = ((isMobile && avoiding) || mini) && frameVisible && frameEl != null;
  useEffect(() => {
    const { setBottomInset, setFrame } = useDockAvoidStore.getState();
    if (!covering || !frameEl) {
      setBottomInset(0);
      setFrame(null);
      return;
    }
    const update = () => {
      const h = frameEl.offsetHeight;
      setBottomInset(isMobile && h > 0 ? h + MOBILE_DOCK_GAP : 0);
      const r = frameEl.getBoundingClientRect();
      setFrame({ left: r.left, right: r.right, top: r.top });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(frameEl);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      setBottomInset(0);
      setFrame(null);
    };
  }, [covering, frameEl, isMobile]);

  // The dock emptied (stopped, logout, the last cast left): the next player opens in the floating dock.
  const dockEmpty = !liveShowing && casts.length === 0 && !wall && !chatLink;
  useEffect(() => {
    if (dockEmpty && useDockMiniStore.getState().minimised) useDockMiniStore.getState().open();
  }, [dockEmpty]);

  // A swipe up on the mini player opens the dock (touch).
  const [swipeFrom, setSwipeFrom] = useState<number | null>(null);

  if (!liveShowing && casts.length === 0 && !wall && !chatLink) return <>{pipHost}</>;

  // The chat link card's Go to message: open the conversation, whose body jumps to the message.
  function goToChatMessage(np: ChatLinkNowPlaying) {
    const path = chatMessagePath(np);
    if (!path) return;
    useChatLinkPlaybackStore.getState().requestJump(np);
    navigate(path);
  }

  function canPopOut(cast: DockCast, slot: StreamSlot): boolean {
    if (target?.kind !== "cast" || target.key !== cast.key || popped?.key === cast.key) return false;
    // Only file videos pop out: YouTube embeds fail with error 153 inside a Document PiP window.
    // Without Document PiP the fallback is <video>.requestPictureInPicture().
    return slot.streamContentType === "VMPL_FILE" && slot.mediaType !== "IMAGE";
  }

  async function popOut(cast: DockCast) {
    if (isDocumentPipSupported()) {
      const win = await openPipWindow(PIP_WINDOW_SIZE);
      if (win) {
        setPopped({ key: cast.key, win });
        return;
      }
    }
    document
      .querySelector<HTMLVideoElement>(`[data-cast-card="${cast.key}"] video`)
      ?.requestPictureInPicture()
      .catch((err) => logger.warn("[PiP] Native picture-in-picture failed", err));
  }

  function expand(cast: DockCast) {
    // Back in the app window: the effect above closes the PiP window.
    if (popped?.key === cast.key) setPopped(null);
    navigate(`/street-chat?conv=${cast.gcId}&panel=CHANNELS`);
  }

  const frameStyle: React.CSSProperties = mini
    ? isMobile
      ? { position: "fixed", left: 8, right: 8, bottom: MOBILE_DOCK_BOTTOM, zIndex: Z_DOCK }
      : {
          position: "fixed",
          width: CARD_WIDTH.md,
          maxWidth: "calc(100vw - 24px)",
          zIndex: Z_DOCK,
          // Bottom-right (inside the right column when a panel is open); bottom-left while a post dialog's comments
          // input is at the bottom-right.
          ...cornerStyle(avoiding ? "bl" : "br"),
        }
    : isMobile
    ? { position: "fixed", left: 8, right: 8, ...(avoiding ? { bottom: MOBILE_DOCK_BOTTOM } : { top: MOBILE_DOCK_TOP }), zIndex: Z_DOCK }
    : {
        position: "fixed",
        width: CARD_WIDTH[size],
        maxWidth: "calc(100vw - 24px)",
        zIndex: Z_DOCK,
        ...cornerStyle(shownCorner),
        transform: `translate(${drag.offset.x}px, ${drag.offset.y}px)`,
        transition: drag.dragging ? "none" : "transform 120ms ease-out",
      };

  return (
    <>
      {pipHost}
      <div
        ref={setFrameEl}
        data-testid="stream-dock"
        data-stream-dock
        data-mini={mini ? "true" : undefined}
        className={liveTop ? "rounded-xl bg-[var(--bg-base)] p-2 shadow-lg" : undefined}
        onTouchStart={mini ? (e) => setSwipeFrom(e.touches[0]?.clientY ?? null) : undefined}
        onTouchEnd={
          mini
            ? (e) => {
                const end = e.changedTouches[0]?.clientY;
                if (swipeFrom != null && end != null && swipeFrom - end >= MINI_SWIPE_OPEN) openDock();
                setSwipeFrom(null);
              }
            : undefined
        }
        style={
          mini
            ? {
                ...frameStyle,
                display: frameVisible ? "flex" : "none",
                // The live card shows beside its titles; any other top card draws its own mini form.
                flexDirection: liveTop ? "row" : "column",
                alignItems: liveTop ? "stretch" : undefined,
                gap: liveTop ? 12 : 0,
              }
            : {
                ...frameStyle,
                // A popped-out child alone leaves nothing in the app window; keep it mounted, hide the frame.
                display: frameVisible ? "flex" : "none",
                flexDirection: "column",
                gap: 8,
                // Mobile: one card at most before scrolling, so the chat composer stays reachable.
                maxHeight: isMobile ? "40vh" : "60vh",
                overflowY: "auto",
                // Cards never shrink (shrink-0): a tall stack scrolls here, by touch too, without dragging the page.
                overscrollBehavior: "contain",
                touchAction: "pan-y",
              }
        }
      >
        {/* The live card's header is the drag handle; without it a strip at the top takes the drag. */}
        {!isMobile && !liveInline && !mini && (
          <div
            data-testid="stream-dock-grab"
            onPointerDown={drag.onPointerDown}
            style={{ height: 24, flexShrink: 0, cursor: "grab", touchAction: "none" }}
            className="rounded-md bg-black/40"
          />
        )}
        <div
          ref={setLiveSlotEl}
          data-testid="stream-dock-live"
          // Mini: the live card is a 200px-wide player beside its titles, or hidden behind another player (its sound
          // plays on; it is not YouTube).
          style={mini ? (liveTop ? { width: 200, flexShrink: 0 } : { display: "none" }) : { display: "contents" }}
        />
        {casts.map((cast) => {
          const slot = slotOf(cast);
          if (!slot) return null;
          const card = (
            <CastDockCard
              key={cast.key}
              cast={cast}
              slot={slot}
              controls={
                cast.own
                  ? (positionMs: () => number) => <OwnCastControls localId={cast.localId} positionMs={positionMs} />
                  : <WatchedCastControls castKey={cast.key} />
              }
              onStall={cast.own ? (ms) => useOwnCastStore.getState().reanchor(cast.slotId, ms) : undefined}
              onItemEnded={cast.own && cast.localId === "VMPL_LINK_1" ? (url) => useOwnCastStore.getState().onItemEnded(cast.slotId, url) : undefined}
              onItemFailed={cast.own && cast.localId === "VMPL_LINK_1" ? (url) => useOwnCastStore.getState().onItemFailed(cast.slotId, url) : undefined}
              onExpand={() => expand(cast)}
              onPopOut={canPopOut(cast, slot) ? () => void popOut(cast) : undefined}
              onMinimise={popped?.key === cast.key ? undefined : () => useDockMiniStore.getState().minimise(cast.key)}
              mini={popped?.key === cast.key ? null : miniFor(cast.key)}
              hidden={mini && popped?.key !== cast.key && topKey !== cast.key}
            />
          );
          // Portalling remounts the player: a link reloads its hosted player, a
          // file reloads its <video>; the cast follower resyncs to the timeline.
          return popped?.key === cast.key ? createPortal(card, popped.win.document.body, cast.key) : card;
        })}
        <WallDockCard onGoToPost={goToWallPost} mini={miniFor(WALL_KEY)} hidden={mini && topKey !== WALL_KEY} />
        <ChatLinkDockCard onGoToMessage={goToChatMessage} mini={miniFor(CHAT_LINK_KEY)} hidden={mini && topKey !== CHAT_LINK_KEY} />
        {liveTop && <LiveMiniInfo slotId={liveSlotId} others={players.length - 1} onOpen={openDock} infoByGc={infoByGc} />}
      </div>
    </>
  );
}

/** The mini player's titles beside the live card (the card keeps its own mute / close). */
function LiveMiniInfo({ slotId, others, onOpen, infoByGc }: { slotId: number | null; others: number; onOpen(): void; infoByGc: Parameters<typeof findSlot>[0] }) {
  const located = slotId == null ? undefined : findSlot(infoByGc, slotId);
  const slot = located?.slot;
  const title = slot?.streamingUser?.name ?? "";
  const line = located && slot ? slotLabel(slot, slotEntries(infoByGc[located.gcId]).map((e) => e.slot)) : "";
  return <MiniPlayerInfo mini={{ others, onOpen }} title={title} line={line} video playing onTogglePlay={null} onStop={null} />;
}

/**
 * The own cast's timeline over the slot's when newer: the publisher's player follows the
 * answers the server gave before their event arrives.
 */
function withNewerTimeline(slot: StreamSlot, t: CastTimeline | null): StreamSlot {
  if (!t || !acceptsTimeline(timelineOf(slot), t)) return slot;
  return { ...slot, playStatus: t.status, seekTime: t.seekMs, playStartTime: t.startAt ?? undefined, timelineRev: t.rev ?? undefined };
}
